import axiosInstance from '../utils/axiosConfig';

/**
 * Reporting Central (ledger row 123): one place for the standard report
 * formats and the reports a person builds for themselves.
 *
 * Every rule -- which reports exist, who may run one, which rows it may show,
 * what the file is called -- is decided by the server. This file only carries
 * the answers back and forth. Cookie auth comes from the shared axios instance.
 */
const BASE = '/api/reporting/';

/**
 * A refusal on a file download arrives as a Blob, which the shared error
 * helper cannot read -- so "this report is locked" would surface as a generic
 * failure. Turn it back into the JSON the server sent.
 */
const readBlobError = async (error) => {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      error.response.data = JSON.parse(await data.text());
    } catch (_) {
      // Not JSON: leave it; the caller's fallback sentence applies.
    }
  }
  return error;
};

/**
 * The name the server gave the file. It arrives in Content-Disposition, so a
 * re-download lands beside the first one under the same name.
 */
const fileNameFrom = (response, fallback) => {
  const disposition = response?.headers?.['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1].trim());
  } catch (_) {
    return match[1].trim();
  }
};

/** Hand a downloaded blob to the browser under the server's own file name. */
const saveBlob = (response, fallbackName) => {
  const fileName = fileNameFrom(response, fallbackName);
  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: response.data?.type || 'application/octet-stream' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return fileName;
};

/** The front door: the cards this person may open, plus the four counters. */
export const fetchReportingOverview = async () => {
  const { data } = await axiosInstance.get(`${BASE}overview/`);
  return data;
};

/** One report's columns, period choices and quick filters. */
export const fetchReportDetail = async (reportId) => {
  const { data } = await axiosInstance.get(`${BASE}reports/${reportId}/`);
  return data;
};

/** Run the report for the screen. `inputs` is { period, quick_filters }. */
export const runReport = async (reportId, inputs) => {
  const { data } = await axiosInstance.post(`${BASE}reports/${reportId}/run/`, inputs);
  return data;
};

/**
 * Run it and hand over the file. Returns the name it was saved under, so the
 * screen can say which file it just gave the user.
 */
export const downloadReport = async (reportId, inputs, format) => {
  let response;
  try {
    response = await axiosInstance.post(
      `${BASE}reports/${reportId}/download/`,
      { ...inputs, format },
      { responseType: 'blob' },
    );
  } catch (error) {
    throw await readBlobError(error);
  }
  return saveBlob(response, `report.${format}`);
};

/** What can be reported on, grouped the way the builder shows it. */
export const fetchCatalogue = async () => {
  const { data } = await axiosInstance.get(`${BASE}catalogue/`);
  return data;
};

/**
 * Run a report that has not been saved — a builder draft, or an Ask AI answer.
 * Same engine and same access checks as a saved one; nothing is kept.
 */
export const previewRecipe = async (body) => {
  const { data } = await axiosInstance.post(`${BASE}preview/`, body);
  return data;
};

/** Take the file for an unsaved report. Returns the name it was saved under. */
export const downloadPreview = async (body, format) => {
  let response;
  try {
    response = await axiosInstance.post(
      `${BASE}preview/download/`, { ...body, format }, { responseType: 'blob' },
    );
  } catch (error) {
    throw await readBlobError(error);
  }
  return saveBlob(response, `report.${format}`);
};

/** Save a new report. `origin` is 'builder' or 'ai'. Answers with its card. */
export const createReport = async (body) => {
  const { data } = await axiosInstance.post(`${BASE}reports/`, body);
  return data;
};

/** Change a saved report — any of name, description, columns, filters, sort, totals. */
export const updateReport = async (reportId, patch) => {
  const { data } = await axiosInstance.patch(`${BASE}reports/${reportId}/`, patch);
  return data;
};

export const deleteReport = async (reportId) => {
  await axiosInstance.delete(`${BASE}reports/${reportId}/`);
};

/** Show a report to everyone in Reporting Central, or take it back. */
export const setReportShared = async (reportId, shared) => {
  const { data } = await axiosInstance.post(`${BASE}reports/${reportId}/share/`, { shared });
  return data;
};

/** Make a report one of the standard formats, or put it back. Superusers only. */
export const setReportLocked = async (reportId, locked) => {
  const { data } = await axiosInstance.post(`${BASE}reports/${reportId}/lock/`, { locked });
  return data;
};

/** Copy any report you can open into My reports, as a starting point. */
export const copyReport = async (reportId) => {
  const { data } = await axiosInstance.post(`${BASE}reports/${reportId}/copy/`);
  return data;
};

/**
 * One typed request in, one answer out. This can take 10-30 seconds, so the
 * screen must say what is happening rather than look frozen.
 */
export const askReporting = async (body) => {
  const { data } = await axiosInstance.post(`${BASE}ask/`, body);
  return data;
};

/** This person's recent requests, newest first. */
export const fetchAskSessions = async () => {
  const { data } = await axiosInstance.get(`${BASE}ask/sessions/`);
  return data;
};

/** One past request, with every turn of the conversation. */
export const fetchAskSession = async (sessionId) => {
  const { data } = await axiosInstance.get(`${BASE}ask/sessions/${sessionId}/`);
  return data;
};

export const deleteAskSession = async (sessionId) => {
  await axiosInstance.delete(`${BASE}ask/sessions/${sessionId}/`);
};

/** Past downloads of one report (only the ones this person ran). */
export const fetchReportRuns = async (reportId) => {
  const { data } = await axiosInstance.get(`${BASE}reports/${reportId}/runs/`);
  return data;
};

/** Re-open a past download exactly as it was handed over the first time. */
export const downloadPastRun = async (run) => {
  let response;
  try {
    response = await axiosInstance.get(`${BASE}runs/${run.id}/file/`, { responseType: 'blob' });
  } catch (error) {
    throw await readBlobError(error);
  }
  return saveBlob(response, run.file_name);
};

/* --- Accounting export (ledger 200): a company's month as a journal CSV ------ */

/** The companies this person may export, each with its last export. */
export const fetchAccountingExport = async () => {
  const { data } = await axiosInstance.get(`${BASE}accounting-export/`);
  return data;
};

/** The journal lines the file would hold for { company, month: 'YYYY-MM' }; nothing is written. */
export const previewAccountingExport = async (body) => {
  const { data } = await axiosInstance.post(`${BASE}accounting-export/preview/`, body);
  return data;
};

/**
 * Make the export and take the file. The server records the run and the
 * documents in it, so the same documents never appear in a later export.
 * Returns the name the file was saved under.
 */
export const downloadAccountingExport = async (body) => {
  let response;
  try {
    response = await axiosInstance.post(`${BASE}accounting-export/runs/`, body, { responseType: 'blob' });
  } catch (error) {
    throw await readBlobError(error);
  }
  return saveBlob(response, 'journal.csv');
};

/** The past exports of one company. */
export const fetchAccountingExportRuns = async (companyId) => {
  const { data } = await axiosInstance.get(`${BASE}accounting-export/runs/`, { params: { company: companyId } });
  return data;
};

/** Export again: the file exactly as that run handed it over. */
export const downloadAccountingExportRun = async (run) => {
  let response;
  try {
    response = await axiosInstance.get(`${BASE}accounting-export/runs/${run.id}/file/`, { responseType: 'blob' });
  } catch (error) {
    throw await readBlobError(error);
  }
  return saveBlob(response, run.file_name || 'journal.csv');
};
